import React, { useState, useEffect, useRef } from 'react';
import { Icon, IStyle, mergeStyles } from '@fluentui/react';
import { internal__useBwjScrollContainerStyles } from './styles';

interface IBwjScrollContainerProps {
  containerStyle?: IStyle;
  children?: React.ReactNode;
  invokeScrollReset?: number;
  invokeScrollPositionReport?: number;
  onScrollChanged?: (scrollTop: number, scrollLeft: number, containerWidth: number, containerHeight: number) => void;
}

const SHOW_SCROLL_BUTTON_AFTER_TRAVELLING = 50;

export const BwjScrollContainer: React.FC<IBwjScrollContainerProps> = (props) => {
  const { invokeScrollReset, invokeScrollPositionReport, onScrollChanged } = props;
  const [showVertical, setShowVertical] = useState(false);
  const [showHorizontal, setShowHorizontal] = useState(false);

  useEffect(() => {
    if(!!invokeScrollReset) {
      scrollToTop();
      scrollLeft();
    }
  }, [invokeScrollReset]);

  useEffect(() => {
    const e = containerRef.current;
    if(!!invokeScrollPositionReport && !!onScrollChanged && !!e) {
      onScrollChanged(e.scrollTop, e.scrollLeft, e.clientWidth, e.clientHeight);
    }
  }, [invokeScrollPositionReport, onScrollChanged]);

  const styles = internal__useBwjScrollContainerStyles();
  const containerRef = useRef<HTMLDivElement>(null);
  const timeoutCancel = useRef<number | null>(null);

  const containerStyle: IStyle[] = [styles.defaultContainerStyle];
  !!props.containerStyle && containerStyle.push(props.containerStyle);

  const containerClass = mergeStyles(containerStyle);

    useEffect(() => {

      const onScrollChange = () => {
        timeoutCancel.current = null;
        const e = containerRef.current;
        if(!e) { return; }

        !!onScrollChanged && onScrollChanged(e.scrollTop, e.scrollLeft, e.clientWidth, e.clientHeight);
      };

      const handleScroll = () => {
        if (!containerRef.current) { return; }

        setShowVertical(containerRef.current.scrollTop > SHOW_SCROLL_BUTTON_AFTER_TRAVELLING);
        setShowHorizontal(containerRef.current.scrollLeft > SHOW_SCROLL_BUTTON_AFTER_TRAVELLING);

        if(!!timeoutCancel.current) {
          window.clearTimeout(timeoutCancel.current);
        }
        timeoutCancel.current = window.setTimeout(onScrollChange, 250);
      };
  
      // Attach the scroll event listener
      // window.addEventListener('scroll', handleScroll);
      containerRef.current?.addEventListener('scroll', handleScroll, true);
  
      // Clean up the event listener when the component is unmounted
      const containerRefValue = containerRef.current;
      return () => {
        if(!!timeoutCancel.current) {
          window.clearTimeout(timeoutCancel.current);
        }
        containerRefValue?.removeEventListener('scroll', handleScroll, true);
      };
    }, [containerRef, onScrollChanged]);
  
    const scrollToTop = () => {
      containerRef.current?.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    };

    const scrollLeft = () => {
      containerRef.current?.scrollTo({
        left: 0,
        behavior: 'smooth',
      });
    };

    const verticalButton = showVertical && <div role='button' className={styles.scrollButton} onClick={scrollToTop}><Icon iconName='ChevronUpMed' /></div>;
    const horizontalButton = showHorizontal && <div role='button' className={styles.scrollButton} onClick={scrollLeft}><Icon iconName='ChevronLeftMed' /></div>;
  
    return (
      <div className={containerClass} ref={containerRef}>
        <div className={styles.buttonPositioner}>
          <div className={styles.buttonContainer}>
            {verticalButton}
            {horizontalButton}
          </div>
        </div>
        {props.children}
      </div>
    );
  };
